import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Users, Zap, Shield, TrendingUp } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { subscriptionService } from '../../services/subscriptionService';
import MainLayout from '../../components/layout/MainLayout';

import PricingCard from './components/PricingCard';
import FeatureComparison from './components/FeatureComparison';
import FAQSection from './components/FAQSection';
import TestimonialsSection from './components/TestimonialsSection';

const PricingPlans = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const { subscription, hasActiveSubscription } = useSubscription();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(false);

  const plans = [
    {
      id: 'free',
      name: 'Free',
      icon: Users,
      price: { monthly: 0, annual: 0 },
      description: 'Perfect for getting started with basic payment processing',
      features: [
        { text: '$1,000 spending limit', included: true },
        { text: '10 swaps per month', included: true },
        { text: 'Basic wallet connectivity', included: true },
        { text: 'Standard transaction fees', included: true },
        { text: 'Email support', included: true },
        { text: 'Basic analytics', included: true },
        { text: 'Priority support', included: false },
        { text: 'Advanced analytics', included: false },
        { text: 'Custom integrations', included: false }
      ],
      cta: 'Current Plan',
      recommended: false
    },
    {
      id: 'pro',
      name: 'Pro',
      icon: Crown,
      price: { monthly: 29, annual: 290 },
      description: 'For professionals who need enhanced limits and features',
      features: [
        { text: '$10,000 spending limit', included: true },
        { text: '100 swaps per month', included: true },
        { text: 'Advanced wallet connectivity', included: true },
        { text: 'Reduced transaction fees (0.5% lower)', included: true },
        { text: 'Priority email & chat support', included: true },
        { text: 'Advanced analytics & insights', included: true },
        { text: 'Pro payout management', included: true },
        { text: 'API access', included: true },
        { text: 'Custom integrations', included: false }
      ],
      cta: 'Upgrade to Pro',
      recommended: true,
      badge: 'Most Popular'
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      icon: Shield,
      price: { monthly: 'Custom', annual: 'Custom' },
      description: 'Tailored solutions for large-scale operations',
      features: [
        { text: 'Unlimited spending limit', included: true },
        { text: 'Unlimited swaps', included: true },
        { text: 'White-label solutions', included: true },
        { text: 'Custom transaction fees', included: true },
        { text: 'Dedicated account manager', included: true },
        { text: 'Custom analytics & reporting', included: true },
        { text: 'Advanced payout management', included: true },
        { text: 'Full API access', included: true },
        { text: 'Custom integrations & webhooks', included: true }
      ],
      cta: 'Contact Sales',
      recommended: false
    }
  ];

  const handleUpgrade = async (planId) => {
    if (!user) {
      navigate('/signin');
      return;
    }

    if (planId === 'free') {
      return;
    }

    if (planId === 'enterprise') {
      window.location.href = 'mailto:sales@boltpay.com?subject=Enterprise Plan Inquiry';
      return;
    }

    if (planId === 'pro') {
      setLoading(true);
      try {
        const { data, error } = await subscriptionService?.createCheckoutSession(user?.id, 'pro');
        
        if (error) {
          alert(error?.message || 'Failed to start checkout');
          return;
        }

        if (data?.url) {
          window.location.href = data?.url;
        }
      } catch (err) {
        alert('Failed to start checkout. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const getCurrentPlanId = () => {
    if (hasActiveSubscription && userProfile?.tier === 'pro') {
      return 'pro';
    }
    return 'free';
  };

  const currentPlanId = getCurrentPlanId();
  const annualSavings = Math.round((29 * 12 - 290) / (29 * 12) * 100);

  return (
    <MainLayout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
        {/* Hero Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Choose Your Perfect Plan
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Unlock powerful payment processing with transparent pricing and no hidden fees
            </p>

            {/* Billing Toggle */}
            <div className="flex items-center justify-center gap-4 mb-4">
              <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-gray-900' : 'text-gray-500'}`}>
                Monthly
              </span>
              <button
                onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
                className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                style={{ backgroundColor: billingCycle === 'annual' ? '#3b82f6' : '#d1d5db' }}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    billingCycle === 'annual' ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`text-sm font-medium ${billingCycle === 'annual' ? 'text-gray-900' : 'text-gray-500'}`}>
                Annual
              </span>
              {billingCycle === 'annual' && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  Save {annualSavings}%
                </span>
              )}
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {plans?.map((plan) => (
              <PricingCard
                key={plan?.id}
                plan={plan}
                billingCycle={billingCycle}
                currentPlanId={currentPlanId}
                onUpgrade={handleUpgrade}
                loading={loading}
              />
            ))}
          </div>

          {/* Value Proposition */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-16">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose BOLTPay Pro?</h2>
              <p className="text-gray-600">Unlock advanced features and maximize your payment processing efficiency</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                  <TrendingUp className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">10x Higher Limits</h3>
                <p className="text-gray-600">Process up to $10,000 monthly with 100 swaps included</p>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                  <Zap className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Lower Fees</h3>
                <p className="text-gray-600">Save 0.5% on every transaction with reduced processing fees</p>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                  <Shield className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Priority Support</h3>
                <p className="text-gray-600">Get help faster with dedicated priority support channels</p>
              </div>
            </div>
          </div>

          {/* Feature Comparison */}
          <FeatureComparison plans={plans} />

          {/* Testimonials */}
          <TestimonialsSection />

          {/* FAQ */}
          <FAQSection />

          {/* Security & Compliance */}
          <div className="bg-gray-900 rounded-2xl p-8 text-center text-white">
            <h3 className="text-2xl font-bold mb-4">Enterprise-Grade Security</h3>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              PCI DSS compliant with bank-level encryption. Your data and transactions are always protected.
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-400" />
                <span>PCI DSS Level 1</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-400" />
                <span>256-bit Encryption</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-400" />
                <span>SOC 2 Certified</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-400" />
                <span>GDPR Compliant</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default PricingPlans;